import type {
  ProtoAppearance,
  ProtoMatchGoals,
  ProtoShotPassDetail,
  ProtoLeagueStanding,
  ProtoFixture,
  ProtoComparisonPlayer,
} from "@catbento/shared";
import { LIVE } from "../live/flag.js";
import { SNAPSHOT_TRIPWIRE } from "./snapshot-tripwire.js";

/** One competition-season's league block in `leagueTables[comp][seasonCode]`. */
export interface RawLeagueBlock {
  leagueCode: string;
  matchCount: number;
  table: ProtoLeagueStanding[];
}

// The typed accessor for the prototype snapshot (`window.CatalystData`) — the
// single place the v3 data hooks read the raw blob. Phase 02 wraps every read
// behind a named hook (docs/frontend-v3/plan.md §2); this is the bottom of that
// stack. Only the fields the hooks read are typed; the snapshot carries much
// more (seasons, radarMetrics, …) which later hooks add as they need them, hence
// the `[key: string]: unknown` escape hatches.

/** One season's block in `player.seasons[code]`. */
export interface RawSeason {
  /** metricKey → value for this season (the flat stats map). */
  stats?: Record<string, number | null>;
  /** per-axis radar values (numbers); authoritative over `stats` where present. */
  radarMetrics?: Record<string, number | null>;
  positionMinutes?: unknown;
  [key: string]: unknown;
}

/**
 * A raw per-player record in `window.CatalystData.players[name]`. Loose on
 * purpose — the pure lookups narrow it into the frozen `Proto*` contracts.
 */
export interface RawPlayerRecord {
  /** The player's Catalyst DB id, as a string in the JSON (e.g. "15620") — read it through
   *  `playerIdOf` (data/players.ts), which turns it into the app's numeric PlayerId. */
  id?: string;
  name?: string;
  nickname?: string | null;
  positionTemplate?: string | null;
  team?: string | null;
  competition?: string | null;
  country?: string | null;
  dob?: string | null;
  height?: number | null;
  weight?: number | null;
  mostRecentMatchDate?: string | null;
  mostRecentTeam?: string | null;
  hopsRating?: { rating?: number } | null;
  currentSeason?: string;
  seasons?: Record<string, RawSeason>;
  /** Flat per-match appearance rows (Catalyst's capture; current season only). */
  appearances?: ProtoAppearance[];
  [key: string]: unknown;
}

/** The prototype snapshot object assigned to `window.CatalystData`. */
export interface CatalystSnapshot {
  players: Record<string, RawPlayerRecord>;
  /** Stat category config (appearance/passing/attacking/defending/goalkeeping). */
  metricCategories?: unknown[];
  /** Goal-by-goal timelines, keyed by matchId (string keys in the JSON object). */
  matchGoals?: Record<string, ProtoMatchGoals>;
  /** Shot/pass event detail, keyed by player id (string keys); sparse. */
  shotPassDetail?: Record<string, ProtoShotPassDetail>;
  /** League tables, keyed by competition name → seasonCode → block. */
  leagueTables?: Record<string, Record<string, RawLeagueBlock>>;
  /** Match-results fixtures, keyed by competition name → a flat array. */
  matchResults?: Record<string, ProtoFixture[]>;
  /** Comparison scatter dataset, keyed by competition name. */
  comparisonPopulation?: Record<string, ProtoComparisonPlayer[]>;
  [key: string]: unknown;
}

/**
 * The snapshot's players — none at all in live mode, where the snapshot never loads (step 1).
 * For the canvas code both modes share that asks "is this a snapshot player?" (the pickers,
 * drops, merges): with ?live=1 every player is a live pick, so the answer is always no.
 */
export function snapshotPlayersOrNone(): Record<string, RawPlayerRecord> {
  return LIVE ? {} : readSnapshot().players;
}

/**
 * Read the snapshot off `window`. Fails fast if it's absent — that means the
 * legacy `catalyst-data.js` hasn't loaded, which is a real bug, not a state to
 * paper over with a fallback (repo invariant: no silent fallbacks).
 */
export function readSnapshot(): CatalystSnapshot {
  // The tripwire (step 1, the no-snapshot build): live mode never downloads the snapshot,
  // so a read here is a leftover that would silently show demo data next to real data.
  // Throw loudly so it's found (in a check, or by the widget breaking), never papered over.
  if (LIVE) {
    throw new Error(`?live=1 read the snapshot: ${SNAPSHOT_TRIPWIRE} (data/snapshot.ts readSnapshot).`);
  }
  const data = window.CatalystData as CatalystSnapshot | undefined;
  if (!data || !data.players) {
    throw new Error(
      "window.CatalystData is not loaded — the prototype snapshot (catalyst-data.js) must run before any data hook."
    );
  }
  return data;
}
